import { Download as DownloadIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useMemo, useState } from "react";
import { apiRequest } from "../utils/apiRequest";
import { PAGE_SIZES, usePersistedPagination } from "../utils/usePersistedPagination";
import { showToast } from "../utils/toastOptions";

const WARNING_WINDOW_DAYS = 30;
const PROJECTS_VISIBLE = 3;

const formatLastLogin = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatDays = (days) => {
  if (days < 0) {
    const abs = Math.abs(days);
    return `${abs}d overdue`;
  }
  if (days === 0) return "Today";
  return `${days}d left`;
};

const urgencyColor = (days) => {
  if (days < 0) return { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" };
  if (days <= 7) return { bg: "#fff7ed", text: "#9a3412", border: "#fed7aa" };
  if (days <= 14) return { bg: "#fffbeb", text: "#92400e", border: "#fde68a" };
  return { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename, rows) => {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export function computeExpiringCandidates({
  companyUsers,
  projects,
  companyThreshold,
  warningWindowDays = WARNING_WINDOW_DAYS,
  includeWhitelisted = false,
}) {
  if (!companyThreshold || !Array.isArray(projects) || !companyUsers) return [];
  const projectsWithThreshold = projects.map((p) => ({
    project_id: p.id,
    project_name: p.name,
    threshold: p.inactive_days_threshold ?? companyThreshold,
  }));

  const results = [];
  for (const user of companyUsers) {
    if (user.is_active === false) continue;
    if (user.is_whitelisted && !includeWhitelisted) continue;
    
    let lastActivityDate = user.last_login_at ? new Date(user.last_login_at) : NaN;
    const lastActivatedDate = user.last_activated_at ? new Date(user.last_activated_at) : new Date(user.created_at);
    if (isNaN(lastActivityDate) || (lastActivatedDate && lastActivatedDate > lastActivityDate)) {
      lastActivityDate = lastActivatedDate;
    }

    if (!lastActivityDate || isNaN(new Date(lastActivityDate).getTime())) continue;
    const daysInactive = Math.floor((Date.now() - lastActivityDate) / (1000 * 60 * 60 * 24));
    if (daysInactive === null) continue;

    const companyRemaining = companyThreshold - daysInactive;
    let warningProjects = [];

    if (companyRemaining < warningWindowDays) {
      warningProjects = projectsWithThreshold.map((p) => ({
        project_id: p.project_id,
        project_name: p.project_name,
        effective_threshold_days: companyThreshold,
        days_until_removal: companyRemaining,
      }));
    } else {
      for (const p of projectsWithThreshold) {
        const pRemaining = p.threshold - daysInactive;
        if (pRemaining < warningWindowDays) {
          warningProjects.push({
            project_id: p.project_id,
            project_name: p.project_name,
            effective_threshold_days: p.threshold,
            days_until_removal: pRemaining,
          });
        }
      }
    }

    if (!warningProjects.length) continue;

    warningProjects.sort((a, b) => a.days_until_removal - b.days_until_removal);
    results.push({
      user_id: user.id,
      name: user.name || "",
      email: user.email_address || "",
      last_login_at: user.last_login_at,
      last_activated_at: user.last_activated_at,
      created_at: user.created_at,
      is_whitelisted: !!user.is_whitelisted,
      days_until_inactive: warningProjects[0].days_until_removal,
      projects: warningProjects,
    });
  }
  results.sort((a, b) => a.days_until_inactive - b.days_until_inactive);
  return results;
}

export default function ExpiringAccessTab({
  companyId,
  isAdmin,
  companyUsers = [],
  projects = [],
  companyThreshold,
  onAfterRemove,
  paginationKey,
}) {
  const [pagination, setPagination] = usePersistedPagination(paginationKey);
  const [actingUserId, setActingUserId] = useState(null);
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [hideWhitelisted, setHideWhitelisted] = useState(true);
  const [rangeMin, setRangeMin] = useState("");
  const [rangeMax, setRangeMax] = useState("");

  const allCandidates = useMemo(
    () =>
      computeExpiringCandidates({
        companyUsers,
        projects,
        companyThreshold,
        warningWindowDays: companyThreshold || 365,
        includeWhitelisted: true,
      }),
    [companyUsers, projects, companyThreshold]
  );

  const users = useMemo(() => {
    let filtered = allCandidates;
    if (hideWhitelisted) {
      filtered = filtered.filter((u) => !u.is_whitelisted);
    }
    const min = rangeMin !== "" ? Number(rangeMin) : null;
    const max = rangeMax !== "" ? Number(rangeMax) : null;
    if (min !== null && !Number.isNaN(min)) {
      filtered = filtered.filter((u) => u.days_until_inactive >= min);
    }
    if (max !== null && !Number.isNaN(max)) {
      filtered = filtered.filter((u) => u.days_until_inactive <= max);
    }
    return filtered;
  }, [allCandidates, hideWhitelisted, rangeMin, rangeMax]);

  const overdueCount = useMemo(
    () => users.filter((u) => u.days_until_inactive < 0).length,
    [users]
  );

  const toggleExpanded = useCallback((userId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const handleExport = useCallback(() => {
    if (!users.length) return;
    const header = [
      "Name",
      "Email",
      "Last Login",
      "Last Activated",
      "Created At",
      "Status",
      "Days Until Termination",
      "Projects at Risk",
      "Actions"
    ];
    const dataRows = users.map((u) => [
      u.name || "",
      u.email || "",
      formatLastLogin(u.last_login_at),
      formatLastLogin(u.last_activated_at),
      formatLastLogin(u.created_at),
      u.days_until_inactive < 0 ? "Overdue" : "Expiring",
      Math.abs(u.days_until_inactive),
      (u.projects || []).map((p) => `${p.project_name} (${formatDays(p.days_until_removal)})`).join("; "),
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`expiring-access-company-${companyId}-${stamp}.csv`, [header, ...dataRows]);
  }, [users, companyId]);

  const handleRemoveNow = useCallback(
    async (row) => {
      if (!isAdmin || actingUserId) return;
      const projectIds = (row.projects || []).map((p) => p.project_id);
      if (!projectIds.length) return;
      setActingUserId(row.user_id);
      try {
        await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/companies/${companyId}/users/bulk_remove/`,
          {
            method: "POST",
            body: {
              users: projectIds.map((pid) => ({
                user_id: String(row.user_id),
                project_id: String(pid),
              })),
            },
          }
        );
        showToast(
          "success",
          `Removed ${row.name || "user"} from ${projectIds.length} project${
            projectIds.length === 1 ? "" : "s"
          }`
        );
        if (onAfterRemove) await onAfterRemove();
      } catch (error) {
        showToast("error", error.message || "Failed to remove user");
      } finally {
        setActingUserId(null);
      }
    },
    [actingUserId, companyId, isAdmin, onAfterRemove]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        minSize: 180,
        size: 220,
        Cell: ({ cell, row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {cell.getValue() || "—"}
            </Typography>
            {row.original.is_whitelisted && (
              <Chip
                size="small"
                label="Whitelisted"
                sx={{
                  height: 20,
                  fontSize: "0.675rem",
                  fontWeight: 500,
                  bgcolor: "#eff6ff",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe",
                }}
              />
            )}
          </Stack>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        minSize: 220,
        size: 260,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        id: "last_login_at",
        header: "Last Login",
        size: 180,
        accessorFn: (row) => row.last_login_at,
        Cell: ({ row: tableRow }) => {
          if (tableRow.original.last_login_at) {
            const lastLogin = formatLastLogin(tableRow.original.last_login_at);
            return (
              <Typography variant="body2" color="text.secondary">
                {lastLogin}
              </Typography>
            );
          } else if (tableRow.original.last_activated_at) {
            const activated = formatLastLogin(tableRow.original.last_activated_at);
              return (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  {activated !== "—" ? `Never (activated at ${activated})` : "Never"}
                </Typography>
              );
          } else if (tableRow.original.created_at) {
            const created = formatLastLogin(tableRow.original.created_at);
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {created !== "—" ? `Never (created at ${created})` : "Never"}
              </Typography>
            );
          }
          return "—";
        },
        enableColumnFilter: false,
      },
      {
        id: "last_activated_at",
        header: "Last Activated",
        size: 160,
        accessorFn: (row) => row.last_activated_at,
        Cell: ({ row: tableRow }) => {
          if (tableRow.original.last_activated_at) {
            const activated = formatLastLogin(tableRow.original.last_activated_at);
            return (
              <Typography variant="body2" color="text.secondary">
                {activated}
              </Typography>
            );
          } else if (tableRow.original.created_at) {
            const created = formatLastLogin(tableRow.original.created_at);
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {created !== "—" ? `Never (created at ${created})` : "Never"}
              </Typography>
            );
          }
          return "—";
        },
        enableColumnFilter: false,
      },
      {
        id: "created_at",
        header: "Created At",
        size: 160,
        accessorFn: (row) => row.created_at,
        Cell: ({ cell }) => formatLastLogin(cell.getValue()),
        enableColumnFilter: false,
      },
      {
        id: "days_until_inactive",
        header: "Days Until Termination",
        size: 170,
        accessorFn: (row) => row.days_until_inactive,
        Cell: ({ cell }) => {
          const days = cell.getValue();
          const colors = urgencyColor(days);
          return (
            <Chip
              size="small"
              label={formatDays(days)}
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                bgcolor: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                fontVariantNumeric: "tabular-nums",
              }}
            />
          );
        },
        enableColumnFilter: false,
      },
      {
        id: "projects",
        header: "Projects at Risk",
        size: 320,
        accessorFn: (row) => (row.projects || []).length,
        Cell: ({ row }) => {
          const projectList = row.original.projects || [];
          if (!projectList.length) return "—";
          const userId = row.original.user_id;
          const isExpanded = expandedRows.has(userId);
          const visible = isExpanded ? projectList : projectList.slice(0, PROJECTS_VISIBLE);
          const hiddenCount = projectList.length - visible.length;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.25 }}>
              {visible.map((p) => {
                const pColors = urgencyColor(p.days_until_removal);
                return (
                  <Tooltip
                    key={p.project_id}
                    title={`${p.project_name} — ${formatDays(p.days_until_removal)}`}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={p.project_name}
                      sx={{
                        maxWidth: 200,
                        fontSize: "0.75rem",
                        borderColor: pColors.border,
                        color: pColors.text,
                        bgcolor: pColors.bg,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Tooltip>
                );
              })}
              {hiddenCount > 0 && (
                <Link
                  component="button"
                  underline="hover"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(userId);
                  }}
                  sx={{ fontSize: "0.75rem", ml: 0.5, color: "text.secondary" }}
                >
                  +{hiddenCount} more
                </Link>
              )}
              {isExpanded && projectList.length > PROJECTS_VISIBLE && (
                <Link
                  component="button"
                  underline="hover"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(userId);
                  }}
                  sx={{ fontSize: "0.75rem", ml: 0.5, color: "text.secondary" }}
                >
                  Show less
                </Link>
              )}
            </Stack>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          if (!isAdmin) return null;
          const isActing = actingUserId === row.original.user_id;
          return (
            <Button
              size="small"
              variant="text"
              disabled={!!actingUserId}
              onClick={() => handleRemoveNow(row.original)}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 500,
                color: isActing ? "text.disabled" : "error.main",
                "&:hover": { bgcolor: "error.50", color: "error.dark" },
              }}
            >
              {isActing ? "Removing…" : "Remove"}
            </Button>
          );
        },
      },
    ],
    [actingUserId, expandedRows, handleRemoveNow, isAdmin, toggleExpanded]
  );

  const table = useMaterialReactTable({
    columns,
    data: users,
    getRowId: (row) => String(row.user_id),
    enableColumnActions: false,
    enableSorting: true,
    enableRowSelection: false,
    enablePagination: true,
    enableColumnResizing: true,
    layoutMode: "grid",
    enableColumnFilters: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    positionGlobalFilter: "left",
    muiSearchTextFieldProps: {
      placeholder: "Search by name or email",
      size: "small",
      variant: "outlined",
    },
    state: { pagination },
    onPaginationChange: setPagination,
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: PAGE_SIZES },
    muiBottomToolbarProps: {
      sx: {
        minHeight: "auto",
        "& > .MuiBox-root > .MuiBox-root": { position: "relative", top: "auto", right: "auto" },
        "& .MuiTablePagination-root": { py: 0 },
      },
    },
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      sorting: [{ id: "days_until_inactive", desc: false }],
      showGlobalFilter: true,
      density: "comfortable",
      columnVisibility: {
        last_activated_at: false,
        created_at: false,
      },
    },
    muiTablePaperProps: { elevation: 0, variant: "outlined", sx: { borderRadius: 2 } },
    muiTableHeadCellProps: {
      sx: {
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "text.secondary",
        py: 1.5,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        ...(row.original.days_until_inactive < 0 && {
          bgcolor: "rgba(239, 68, 68, 0.03)",
        }),
      },
    }),
    renderTopToolbarCustomActions: () => (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
          {users.length} user{users.length !== 1 ? "s" : ""}
        </Typography>
        {overdueCount > 0 && (
          <Chip
            size="small"
            label={`${overdueCount} overdue`}
            color="error"
            variant="outlined"
            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
          />
        )}
      </Stack>
    ),
    renderToolbarInternalActions: ({ table: t }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <TextField
            size="small"
            type="number"
            placeholder="Min"
            value={rangeMin}
            onChange={(e) => setRangeMin(e.target.value)}
            sx={{ width: 72, "& .MuiInputBase-input": { py: 0.75, fontSize: "0.8rem" } }}
            InputProps={{ inputProps: { step: 1 } }}
          />
          <Typography variant="caption" color="text.secondary">–</Typography>
          <TextField
            size="small"
            type="number"
            placeholder="Max"
            value={rangeMax}
            onChange={(e) => setRangeMax(e.target.value)}
            sx={{ width: 72, "& .MuiInputBase-input": { py: 0.75, fontSize: "0.8rem" } }}
            InputProps={{ inputProps: { step: 1 } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>days</Typography>
        </Stack>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideWhitelisted}
              onChange={(e) => setHideWhitelisted(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Hide whitelisted</Typography>}
          sx={{ ml: 0, mr: 0.5 }}
        />
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Export current view to CSV">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!users.length}
              sx={{ textTransform: "none", fontWeight: 500, fontSize: "0.8rem" }}
            >
              Export
            </Button>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={t} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ShowHideColumnsButton table={t} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleDensePaddingButton table={t} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFullScreenButton table={t} />
      </Box>
    ),
    positionToolbarAlertBanner: "none",
    renderEmptyRowsFallback: () => (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No users approaching inactivation.
        </Typography>
      </Box>
    ),
  });

  return (
    <Box>
      <MaterialReactTable table={table} />
    </Box>
  );
}
